import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApi, useTenantContext } from '../lib/api';

type ConfigSource = 'draft' | 'published';

type SimulationLog = {
  event: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  metadata?: Record<string, unknown>;
};

type SimulationResult = {
  dry_run: boolean;
  input: { message: string; message_type: string };
  tenant: { tenant_id: string; name: string };
  config: {
    source: ConfigSource;
    version: string | null;
    schema_version: string;
    vertical: string;
  };
  vertical_pack: {
    id: string | null;
    version: string | null;
    vertical: string;
    supported_intents: string[];
  };
  intent: string;
  payload: unknown;
  missing_fields: unknown;
  policy_decision: {
    action: string;
    reason: string;
    priority: string;
    metadata: Record<string, unknown>;
  };
  template: {
    key: string | null;
    variables: Record<string, unknown>;
    reply_send: boolean;
    rendered_text: string | null;
  };
  handoff: { needed: boolean; reason: string | null };
  prompt: {
    provider: string | null;
    model: string | null;
    prompt_id: string | null;
    prompt_version: string | null;
    prompt_hash: string | null;
    prompt_ref: string | null;
  };
  ai: { output: unknown; telemetry: unknown; skipped: boolean; privacy: unknown };
  case_projection: {
    case_id: string;
    status: string;
    priority: string;
    intent: string;
    payload: unknown;
    needs_human_attention: boolean;
    outcome: string | null;
    audit: unknown[];
  };
  response: unknown;
  logs: SimulationLog[];
};

function prettyJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}

function sourceLabel(source: ConfigSource) {
  return source === 'draft' ? 'Draft' : 'Published';
}

export default function SimulationPage() {
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [message, setMessage] = useState('Quero fazer aula experimental.');
  const [configSource, setConfigSource] = useState<ConfigSource>('draft');
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function runSimulation(event: FormEvent) {
    event.preventDefault();
    if (!tenant_id || !hasScope('CONFIG_CHANGE')) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const out = await request<SimulationResult>(
        `/tenants/${encodeURIComponent(tenant_id)}/simulations`,
        {
          method: 'POST',
          body: JSON.stringify({
            message,
            config_source: configSource,
            message_type: 'text',
          }),
        },
      );
      setResult(out);
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setLoading(false);
    }
  }

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;
  if (!hasScope('CONFIG_CHANGE'))
    return <main className="page">Sem permissao para simulacao.</main>;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Simulation Studio</h1>
          <p>Tenant {tenant_id}</p>
        </div>
        <div className="header-actions">
          <Link className="nav-link" to="/config">
            Configuracao
          </Link>
          <Link className="nav-link" to="/">
            Inbox
          </Link>
        </div>
      </header>

      <section className="simulation-layout">
        <form className="panel simulation-form" onSubmit={runSimulation}>
          <h2>Entrada</h2>
          <label>
            Config
            <div className="segmented-control">
              {(['draft', 'published'] as const).map((source) => (
                <button
                  key={source}
                  type="button"
                  className={configSource === source ? 'active' : ''}
                  onClick={() => setConfigSource(source)}
                >
                  {sourceLabel(source)}
                </button>
              ))}
            </div>
          </label>
          <label>
            Mensagem
            <textarea
              rows={7}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </label>
          <button type="submit" disabled={!message.trim() || loading}>
            {loading ? 'Simulando...' : 'Simular'}
          </button>
        </form>

        <section className="simulation-results">
          {error ? <p className="error">{error}</p> : null}
          {!result && !error ? <p className="hint">Nenhuma simulacao executada.</p> : null}

          {result ? (
            <>
              <div className="metric-grid simulation-summary">
                <article className="metric-card">
                  <span>Intencao</span>
                  <strong>{result.intent}</strong>
                </article>
                <article className="metric-card">
                  <span>Policy</span>
                  <strong>{result.policy_decision.action}</strong>
                </article>
                <article className="metric-card">
                  <span>Config</span>
                  <strong>{result.config.source}</strong>
                </article>
                <article className="metric-card">
                  <span>Prompt</span>
                  <strong>{result.prompt.prompt_version ?? '-'}</strong>
                </article>
              </div>

              <div className="simulation-result-grid">
                <section className="panel">
                  <h2>Config usada</h2>
                  <dl>
                    <dt>Tenant</dt>
                    <dd>{result.tenant.name}</dd>
                    <dt>Versao</dt>
                    <dd>{result.config.version ?? '-'}</dd>
                    <dt>Vertical</dt>
                    <dd>{result.config.vertical}</dd>
                    <dt>Pack</dt>
                    <dd>
                      {result.vertical_pack.id ?? '-'} / {result.vertical_pack.version ?? '-'}
                    </dd>
                  </dl>
                </section>

                <section className="panel">
                  <h2>Policy Engine</h2>
                  <dl>
                    <dt>Acao</dt>
                    <dd>{result.policy_decision.action}</dd>
                    <dt>Motivo</dt>
                    <dd>{result.policy_decision.reason}</dd>
                    <dt>Prioridade</dt>
                    <dd>{result.policy_decision.priority}</dd>
                    <dt>Handoff</dt>
                    <dd>{result.handoff.needed ? (result.handoff.reason ?? '-') : 'Nao'}</dd>
                  </dl>
                </section>

                <section className="panel">
                  <h2>Template</h2>
                  <dl>
                    <dt>Template</dt>
                    <dd>{result.template.key ?? '-'}</dd>
                    <dt>Enviar</dt>
                    <dd>{result.template.reply_send ? 'Sim' : 'Nao'}</dd>
                  </dl>
                  <p>{result.template.rendered_text ?? '-'}</p>
                </section>

                <section className="panel">
                  <h2>Prompt</h2>
                  <dl>
                    <dt>Provider</dt>
                    <dd>{result.prompt.provider ?? '-'}</dd>
                    <dt>Modelo</dt>
                    <dd>{result.prompt.model ?? '-'}</dd>
                    <dt>ID</dt>
                    <dd>{result.prompt.prompt_id ?? '-'}</dd>
                    <dt>Versao</dt>
                    <dd>{result.prompt.prompt_version ?? '-'}</dd>
                  </dl>
                </section>
              </div>

              <section className="panel simulation-json-grid">
                <div>
                  <h2>Payload</h2>
                  <pre className="json-preview">{prettyJson(result.payload)}</pre>
                </div>
                <div>
                  <h2>Campos faltantes</h2>
                  <pre className="json-preview">{prettyJson(result.missing_fields)}</pre>
                </div>
                <div>
                  <h2>Decisao completa</h2>
                  <pre className="json-preview">{prettyJson(result.policy_decision)}</pre>
                </div>
                <div>
                  <h2>Case simulado</h2>
                  <pre className="json-preview">{prettyJson(result.case_projection)}</pre>
                </div>
              </section>

              <section className="panel simulation-log">
                <h2>Logs de decisao</h2>
                <ol>
                  {result.logs.map((log) => (
                    <li key={log.event}>
                      <strong>{log.event}</strong>
                      <span>{log.message}</span>
                      {log.metadata ? <pre>{prettyJson(log.metadata)}</pre> : null}
                    </li>
                  ))}
                </ol>
              </section>
            </>
          ) : null}
        </section>
      </section>
    </main>
  );
}
